import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["field", "icon", "tooltip"];
  static values = {
    serverUrl: String,
    app: String,
    id: String,
    fieldName: String,
    serverExtra: String,
    countryId: String,

    required: { type: Boolean, default: false },
    on: { type: String, default: "both" },
    debounce: { type: Number, default: 400 },
    enabled: { type: Boolean, default: true },

    minLength: Number,
    maxLength: Number,
    pattern: String, // e.g. "^[0-9]{14}$" or "/^[0-9]{14}$/"
    contains: String,

    requireLinkedin: { type: Boolean, default: false }, // active la vérif LinkedIn
    allowedHosts: String, // "linkedin.com, fr.linkedin.com, www.linkedin.com"
    normalizeUrl: { type: Boolean, default: true }, // force https et www si besoin
    tooltipTargetId: String,
  };

  connect() {
    this.timeout = null;
    this.tooltipTimeout = null;
    this.successTimeout = null;
    this.lastCheckedValue = (this.fieldTarget.value || "").trim();
    if (this.hasIconTarget) this.iconTarget.style.fontSize = "1.2em";

    if (this.onValue === "input" || this.onValue === "both") {
      this.fieldTarget.addEventListener("input", this.onInput);
    }
    if (this.onValue === "blur" || this.onValue === "both") {
      this.fieldTarget.addEventListener("blur", this.onBlur);
    }
    if (this.hasIconTarget && this.hasTooltipTarget) {
      this.iconTarget.addEventListener("mouseenter", this.onEnterIcon);
      this.iconTarget.addEventListener("mouseleave", this.onLeaveIcon);
    }

    // 🔥 Validation auto quand le champ est là
    const initialVal = (this.fieldTarget.value || "").trim();
    if (this.enabledValue && (initialVal.length > 0 || this.requiredValue)) {
      // petit délai pour laisser le DOM se peindre
      setTimeout(() => this.validate(true), 0);
    }
  }


  disconnect() {
    clearTimeout(this.timeout);
    clearTimeout(this.tooltipTimeout);
    this.fieldTarget.removeEventListener?.("input", this.onInput);
    this.fieldTarget.removeEventListener?.("blur", this.onBlur);
    if (this.hasIconTarget && this.hasTooltipTarget) {
      this.iconTarget.removeEventListener?.("mouseenter", this.onEnterIcon);
      this.iconTarget.removeEventListener?.("mouseleave", this.onLeaveIcon);
    }
  }

  // Handlers
  onInput = () => {
    if (!this.enabledValue) return;
    clearTimeout(this.timeout);
    clearTimeout(this.tooltipTimeout);
    this.timeout = setTimeout(() => this.validate(false), this.debounceValue);
  };
  onBlur = () => {
    if (!this.enabledValue) return;
    const val = (this.fieldTarget.value || "").trim();
    if (val !== this.lastCheckedValue) {
      this.validate(true);
    } else if (this.hasTooltipTarget && this.tooltipTarget.textContent) {
      this.tooltipTimeout = setTimeout(() => this.hideTooltip(), 2000);
    }
  };
  onEnterIcon = () => {
    if (this.hasTooltipTarget && this.tooltipTarget.textContent)
      this.tooltipTarget.style.display = "block";
  };
  onLeaveIcon = () => this.hideTooltip();

  // Core

  validate(fromBlur = false) {
    const val = (this.fieldTarget.value || "").replace(/\s+/g, "").trim();
    this.lastCheckedValue = val;

    const skipValues = ["N/A", "NA", "N/C", "NC"];

    // required
    if (this.requiredValue && val.length === 0) {
      return this.setError("Champ requis", fromBlur);
    }
    if (!this.requiredValue && val.length === 0) return this.setNeutral();

    // --- Client checks (before server) ---
    // longueur min/max
    const attrMinLen = parseInt(this.fieldTarget.getAttribute("minlength"), 10);
    const attrMaxLen = parseInt(this.fieldTarget.getAttribute("maxlength"), 10);
    const minL = this.hasMinLengthValue
      ? this.minLengthValue
      : Number.isFinite(attrMinLen)
      ? attrMinLen
      : null;
    const maxL = this.hasMaxLengthValue
      ? this.maxLengthValue
      : Number.isFinite(attrMaxLen)
      ? attrMaxLen
      : null;

    // ignorer min/max si val fait partie des skipValues
    if (!skipValues.includes(val.toUpperCase())) {
      if (Number.isFinite(minL) && val.length < minL) {
        return this.setError(`Minimum ${minL} caractères`, fromBlur);
      }
      if (Number.isFinite(maxL) && val.length > maxL) {
        return this.setError(`Maximum ${maxL} caractères`, fromBlur);
      }
    }

    // regex pattern
    if (this.hasPatternValue && this.patternValue) {
      let re;
      const m = String(this.patternValue).match(/^\/(.+)\/([a-z]*)$/i);
      try {
        re = m ? new RegExp(m[1], m[2]) : new RegExp(this.patternValue);
      } catch {
        return this.setError("Motif invalide", fromBlur);
      }
      if (!re.test(val)) return this.setError("Format invalide", fromBlur);
    }

    // --- Vérifier contains (ex: SIRET contient SIREN) ---
    if (this.hasContainsValue) {
      const otherField = document.getElementById(this.containsValue);
      if (otherField) {
        const otherVal = (otherField.value || "").replace(/\s+/g, "").trim();
        if (
          otherVal &&
          !skipValues.includes(val.toUpperCase()) &&
          !skipValues.includes(otherVal.toUpperCase())
        ) {
          if (!val.includes(otherVal)) {
            return this.setError(`Doit contenir ${otherVal}`, fromBlur);
          }
        }
      }
    }
    // --- end client checks ---

    // --- Vérification LinkedIn (optionnelle) ---
    if (this.requireLinkedinValue) {
      let candidate = val;

      if (this.normalizeUrlValue) candidate = this.normalizeToHttps(candidate);

      let url;
      try {
        url = new URL(candidate);
      } catch {
        return this.setError("URL invalide", fromBlur);
      }

      if (!this.isLinkedinHost(url.hostname)) {
        return this.setError("Doit être une URL linkedin.com", fromBlur);
      }
      if (!this.isLikelyLinkedinPath(url.pathname)) {
        return this.setError(
          "Chemin LinkedIn invalide (ex: /in/..., /company/...)",
          fromBlur
        );
      }

      // si tout est ok ET qu’on normalise, on remet la valeur propre dans le champ
      if (this.normalizeUrlValue) {
        this.fieldTarget.value = url.href;
        const msg = "URL LinkedIn valide";
        return this.setSuccess(msg, fromBlur);       
       
      }
    }
    // --- fin vérif LinkedIn ---

    const url = this.composeServerUrl(val);
    if (!url) return this.setNeutral();
    this.setLoading();
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then((r) => r.json())
      .then((json) => this.handleServerResponse(json, fromBlur))
      .catch(() => this.setError("Erreur serveur", fromBlur));
  }

  composeServerUrl(value) {
    if (!this.hasServerUrlValue) return null;
    const prefix = (this.appValue || "").replace(/\/+$/, "");
    const base = this.serverUrlValue.startsWith("/")
      ? this.serverUrlValue
      : `/${this.serverUrlValue}`;
    const qs = new URLSearchParams();
    qs.set("value", value);
    if (this.hasFieldNameValue && this.fieldNameValue)
      qs.set("field", this.fieldNameValue);
    if (this.hasCountryIdValue && this.countryIdValue)
      qs.set("country_id", this.countryIdValue);
    if (this.hasIdValue && this.idValue) qs.set("id", this.idValue);
    if (this.hasServerExtraValue && this.serverExtraValue) {
      const extra = new URLSearchParams(this.serverExtraValue);
      extra.forEach((v, k) => qs.set(k, v));
    }
    return `${prefix}${base}?${qs.toString()}`;
  }

  handleServerResponse(res, fromBlur) {
    const myId = this.hasIdValue ? String(this.idValue) : null;
    let isDup =
      (res.duplicate === true || res.doublon === true) &&
      (!res.id || String(res.id) !== myId);

    const msgs = Array.isArray(res.messages) ? res.messages : [];

    if (isDup) {
      const msg = msgs.length ? msgs[0] : "Doublon détecté";
      this.setError(msg, fromBlur);
      this.dispatch("invalid", {
        detail: { value: this.fieldTarget.value, duplicate: true, server: res },
      });
    } else {
      const msg = msgs.length ? msgs[0] : "Unique";
      this.setSuccess(msg, fromBlur);
      this.dispatch("valid", {
        detail: {
          value: this.fieldTarget.value,
          duplicate: false,
          server: res,
        },
      });
    }
  }

  // UI helpers
  setLoading() {
    // no auto-hide for loading
    clearTimeout(this.tooltipTimeout);
    clearTimeout(this.successTimeout);
    this._tip("fa fa-spinner fa-spin", "#007bff", "Vérification...");
  }

  setSuccess(msg) {
    // show success, then hide EVERYTHING after 2s
    clearTimeout(this.tooltipTimeout);
    clearTimeout(this.successTimeout);

    this._tip("fa fa-check-circle", "#28a745", msg || "OK");

    // this.successTimeout = setTimeout(() => {
    //   this.setNeutral();
    // }, 2000);
  }

  setError(msg) {
    // keep error visible (no auto-hide)
    clearTimeout(this.tooltipTimeout);
    clearTimeout(this.successTimeout);
    this._tip("fa fa-exclamation-circle", "#dc3545", msg || "Invalide");
  }

  setNeutral() {
    clearTimeout(this.tooltipTimeout);
    clearTimeout(this.successTimeout);
    if (this.hasIconTarget) {
      this.iconTarget.className = "";
      this.iconTarget.style.color = "";
    }
    if (this.hasTooltipTarget) {
      this.tooltipTarget.textContent = "";
      this.tooltipTarget.style.display = "none";
    }
  }

 
  // _tip(cls, color, text) {
  //   if (this.hasIconTarget) {
  //     this.iconTarget.className = cls;
  //     this.iconTarget.style.color = color;
  //   }
  //   if (this.hasTooltipTarget) {
  //     this.tooltipTarget.textContent = text;
  //     this.tooltipTarget.style.display = "block";
  //   }
  // }
 
  _tip(cls, color, text) {

  if (this.hasIconTarget) {
    this.iconTarget.className = cls;
    this.iconTarget.style.color = color;
  }

 
  if (this.hasTooltipTarget) {
    this.tooltipTarget.textContent = text || "";
    this.tooltipTarget.style.display = text ? "block" : "none";
  }

  // 2) Sinon, on met à jour le contenu de TON tooltip
  if (!this.hasTooltipTarget && this.hasTooltipTargetIdValue) {
    this._setTipExternal(text || "");
  }
}

  hideTooltip() {
    if (this.hasTooltipTarget) this.tooltipTarget.style.display = "none";
  }

  normalizeToHttps(urlLike) {
    // ajoute https:// si l'utilisateur tape sans schéma
    if (!/^https?:\/\//i.test(urlLike)) return `https://${urlLike}`;
    return urlLike;
  }

  isLinkedinHost(hostname) {
    const baseHosts = ["linkedin.com"]; // par défaut
    const extra =
      this.hasAllowedHostsValue && this.allowedHostsValue
        ? this.allowedHostsValue
            .split(",")
            .map((h) => h.trim().toLowerCase())
            .filter(Boolean)
        : [];
    const allowed = new Set([...baseHosts, ...extra]);

    const hn = String(hostname || "").toLowerCase();
    // ex: www.linkedin.com, fr.linkedin.com -> on valide si ça se termine par ".linkedin.com" ou est exactement "linkedin.com"
    return (
      hn === "linkedin.com" || hn.endsWith(".linkedin.com") || allowed.has(hn)
    );
  }

  isLikelyLinkedinPath(pathname) {
    // on exige un chemin non vide, et idéalement un des segments connus
    // (profil /in/, entreprise /company/, école /school/, vitrine /showcase/, etc.)
    const p = String(pathname || "");
    if (!p || p === "/") return false;
    return /^\/(in|company|school|showcase|pub|sales|groups|events|posts|jobs)/i.test(
      p
    );
  }
 
get externalTooltipEl() {
  if (!this.hasTooltipTargetIdValue) return null;
  return document.getElementById(this.tooltipTargetIdValue);
}

_setTipExternal(text) {
  const el = this.externalTooltipEl;
  if (!el) return;
  el.textContent = String(text || "");
  // Notifie (au cas où ton controller tooltip écoute un refresh)
  this.dispatch("tooltip:update", { detail: { id: this.tooltipTargetIdValue, text } });
}


}
